import {
  useAccountSubscriptionQuery,
  useViewerEntitlementsQuery,
} from "generated.hooks";
import useAuth from "hooks/useAuth";
import React, { createContext, useCallback, useMemo } from "react";

type FeatureCode =
  | "livestream_telemetry"
  | "upload_data_logs"
  | "podium_app"
  | "send_pit_to_car_alerts"
  | "private_events"
  | "export_csv_data_logs"
  | "ai_coaching"
  | "rest_api"
  | "teams";

/**
 * Typed constants for the account attribute keys the UI references. The
 * server's code-defined attribute registry remains the source of truth.
 */
export const ACCOUNT_ATTRIBUTE_INTEGRATION_DEVELOPER = "integration_developer";
export const ACCOUNT_ATTRIBUTE_EVENT_ORGANIZER = "event_organizer";

const SubscriptionContext = createContext<{
  /**
   * Whether the viewer's plan includes [featureCode] (`viewerFeatures`).
   * Plan only: a rule that also accepts an account attribute has its own
   * named value, like `canUseRestApi`. Presentation hint only; the server
   * enforces.
   */
  hasFeature: (featureCode: FeatureCode) => boolean;
  /** Whether the viewer's account carries the enabled attribute [key]. */
  hasAttribute: (key: string) => boolean;
  /**
   * Whether the viewer may use the REST API: the plan's rest_api feature, or
   * the integration_developer attribute in its place. Mirrors the server's
   * gate (RestApiAccessUtils). The rest_api capability is separate and hides
   * the pages entirely. Every REST API page reads this rather than composing
   * the rule itself.
   */
  canUseRestApi: boolean;
  /**
   * Whether the viewer's account may register an OAuth application, or edit
   * one's name, redirect URIs, scopes or origins: the integration_developer
   * attribute only, since other Podium users connect to these applications. A
   * paid plan is not enough. Mirrors the server's gate
   * (RestApiAccessUtils.account_may_publish_oauth_applications).
   */
  canPublishOAuthApplications: boolean;
  fetchAccountSubscription: () => void;
  /** True until the viewer's plan features have loaded; `hasFeature` answers false meanwhile. */
  featuresLoading: boolean;
  isFreePlan: boolean;
  stripeRenewalFailed: boolean;
} | null>(null);

function SubscriptionContextProvider({ children }) {
  const { isSuperuser } = useAuth();
  // Superusers have no plan of their own to gate on; both queries are skipped
  // for them, so every check answers false and the pages fall back to their
  // superuser branches.
  const { data, refetch } = useAccountSubscriptionQuery({
    skip: isSuperuser,
  });
  const {
    data: entitlements,
    refetch: refetchEntitlements,
    loading: entitlementsLoading,
  } = useViewerEntitlementsQuery({
    skip: isSuperuser,
  });

  // Guarded on isSuperuser as well as the skip: Apollo can keep the previous
  // result when a query becomes skipped, and a shell that switches to a
  // superuser must not keep the last regular user's entitlements.
  const features = useMemo(
    () =>
      new Set<string>(isSuperuser ? [] : entitlements?.viewerFeatures ?? []),
    [isSuperuser, entitlements]
  );
  const attributes = useMemo(
    () =>
      new Set<string>(
        isSuperuser ? [] : entitlements?.viewerAccountAttributes ?? []
      ),
    [isSuperuser, entitlements]
  );

  const hasFeature = useCallback(
    (featureCode: string) => features.has(featureCode),
    [features]
  );
  const hasAttribute = useCallback(
    (key: string) => attributes.has(key),
    [attributes]
  );

  const canUseRestApi =
    features.has("rest_api") ||
    attributes.has(ACCOUNT_ATTRIBUTE_INTEGRATION_DEVELOPER);
  const canPublishOAuthApplications = attributes.has(
    ACCOUNT_ATTRIBUTE_INTEGRATION_DEVELOPER
  );

  // A plan change moves the feature set too, so both are refetched together.
  const fetchAccountSubscription = useCallback(() => {
    refetch();
    refetchEntitlements();
  }, [refetch, refetchEntitlements]);

  return (
    <SubscriptionContext.Provider
      value={{
        hasFeature,
        hasAttribute,
        canUseRestApi,
        canPublishOAuthApplications,
        fetchAccountSubscription,
        featuresLoading: !isSuperuser && entitlementsLoading,
        isFreePlan: !!(
          data &&
          data.accountSubscription &&
          data.accountSubscription.subscriptionPlan.name == "FREE"
        ),
        stripeRenewalFailed: !!data?.accountSubscription?.stripeRenewalFailed,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export { SubscriptionContext, SubscriptionContextProvider };
